// /vendor/jobs — pending assignments first as action cards, then the
// already-accepted upcoming work. Pending cards carry Accept / Decline
// buttons that invoke vendor-respond's session ops (accept_session /
// decline_session) — the SAME guarded UPDATE state machine the token
// path uses, just keyed by id + vendor_id.
//
// Race semantics: post-mutation we invalidate the query and rely on the
// edge function's 410/410-state response. If the assignment was
// raced (operator cancelled mid-flow, vendor accepted on another
// device), the toast surfaces "Already handled" and the list refreshes.
//
// Decline copy: a small modal with a textarea matching the token path's
// 2000-char truncation. Reason is optional — operator gets the
// decline_reason if provided; the server-side truncation makes
// over-length input safe rather than rejected.

import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { motion } from 'framer-motion'
import { Briefcase, MapPin, CalendarClock, DollarSign, Loader2, CheckCircle2, XCircle, FileText, AlertCircle, Lock, Plus } from 'lucide-react'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/lib/auth'
import { toast } from 'sonner'
import { formatRelativeDate } from '@/lib/utils'
import { LifecycleChip } from '@/pages/vendor/VendorJobDetail'

const FUNCTIONS_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/vendor-respond`

type LifecycleEvent = 'en_route' | 'on_site' | 'delayed' | 'return_visit_scheduled' | 'completed'

interface VendorJobRow {
  assignment_id: string
  // assignment_status is NULL on self-entered rows (no assignment exists).
  // source is the discriminator; null assignment_status is a structural
  // signal, not a sentinel.
  assignment_status: 'pending' | 'accepted' | 'declined' | 'expired' | 'cancelled' | null
  budget_offered: number | string | null
  assigned_at: string
  responded_at: string | null
  token_expires_at: string | null
  decline_reason: string | null
  vendor_id: string
  workspace_id: string
  // NULL on self-entered (no underlying field_job). Photo / event queries
  // gate on truthiness so the join never fires for self-entered.
  field_job_id: string | null
  title: string | null
  job_status: string | null
  scheduled_date: string | null
  scheduled_start: string | null
  scheduled_end: string | null
  address_line1: string | null
  address_line2: string | null
  address_city: string | null
  address_state: string | null
  address_zip: string | null
  job_notes: string | null
  work_order_number: string | null
  purchase_order_number: string | null
  completed_at: string | null
  latest_event_type: LifecycleEvent | null
  latest_event_at: string | null
  source: 'assigned' | 'self_entered'
}

const DECLINE_MAX = 2000  // matches DECLINE_REASON_MAX in vendor-respond

function formatBudget(b: number | string | null): string | null {
  if (b == null) return null
  const n = typeof b === 'string' ? parseFloat(b) : b
  if (!Number.isFinite(n)) return null
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n)
  } catch { return `$${n.toFixed(2)}` }
}

function formatAddress(j: VendorJobRow): string | null {
  const parts = [j.address_line1, j.address_city, j.address_state].filter(Boolean)
  return parts.length ? parts.join(', ') : null
}

function formatScheduledLine(j: VendorJobRow): string | null {
  if (!j.scheduled_date) return null
  const window = (j.scheduled_start && j.scheduled_end)
    ? `${j.scheduled_start}–${j.scheduled_end}`
    : (j.scheduled_start ?? null)
  return window ? `${formatRelativeDate(j.scheduled_date)} · ${window}` : formatRelativeDate(j.scheduled_date)
}

export default function VendorJobs() {
  const queryClient = useQueryClient()
  const { vendorUser } = useAuth()
  const [declineOpen, setDeclineOpen] = useState<null | VendorJobRow>(null)
  const [declineReason, setDeclineReason] = useState('')
  const [newJobOpen, setNewJobOpen] = useState(false)

  const { data: rows = [], isLoading } = useQuery<VendorJobRow[]>({
    queryKey: ['vendor-jobs'],
    refetchInterval: 60_000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('vendor_jobs')
        .select('*')
        .order('assigned_at', { ascending: false })
      if (error) throw error
      return (data ?? []) as VendorJobRow[]
    },
  })

  // Pending only exists for assigned (no concept of "pending" on a
  // self-entered job — vendors are their own dispatcher).
  const pending = useMemo(
    () => rows.filter(r => r.source === 'assigned' && r.assignment_status === 'pending'),
    [rows]
  )
  // Upcoming = accepted assigned (active job_status) OR scheduled self-entered.
  // Same status split as the assigned arm: complete/cancelled fall to History.
  const upcoming = useMemo(() => rows.filter(r => {
    if (r.source === 'self_entered') return r.job_status === 'scheduled'
    return r.assignment_status === 'accepted'
      && (r.job_status === 'scheduled' || r.job_status === 'in_progress' || r.job_status == null)
  }), [rows])

  const respond = useMutation({
    mutationFn: async ({ op, assignment_id, reason }: { op: 'accept_session' | 'decline_session'; assignment_id: string; reason?: string | null }) => {
      const session = await supabase.auth.getSession()
      const accessToken = session.data.session?.access_token
      if (!accessToken) throw new Error('Not signed in')
      const resp = await fetch(FUNCTIONS_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
          'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY as string,
        },
        body: JSON.stringify({ op, assignment_id, declineReason: reason ?? undefined }),
      })
      const body = await resp.json().catch(() => ({}))
      if (resp.status === 200 && body?.ok) return { kind: 'ok' as const, status: body.status as 'accepted' | 'declined' }
      if (resp.status === 410) return { kind: 'raced' as const, state: body?.state as string, status: body?.status as string }
      throw new Error(body?.error ?? `Request failed (${resp.status})`)
    },
    onSuccess: (res, vars) => {
      if (res.kind === 'ok') {
        toast.success(res.status === 'accepted' ? 'Accepted' : 'Declined')
      } else {
        toast.warning(`Already handled — list refreshed (was ${res.status})`)
      }
      queryClient.invalidateQueries({ queryKey: ['vendor-jobs'] })
      setDeclineOpen(null)
      setDeclineReason('')
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : 'Something went wrong')
    },
  })

  if (isLoading) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {[0, 1].map(i => (
          <div key={i} style={{ height: 130, background: 'var(--surface)', border: '1px solid var(--border-subtle)', borderRadius: 12 }} />
        ))}
      </div>
    )
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      {/* Pending — action cards */}
      <section>
        <header style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 10 }}>
          <h1 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: 'var(--ink-primary)', letterSpacing: '-0.02em' }}>Awaiting your response</h1>
          <span style={{ fontSize: 12, color: 'var(--ink-tertiary)' }}>{pending.length} pending</span>
        </header>
        {pending.length === 0 ? (
          <div style={{ padding: 20, background: 'var(--surface)', border: '1px solid var(--border-subtle)', borderRadius: 12, color: 'var(--ink-secondary)', fontSize: 13 }}>
            All caught up — no pending invitations right now.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {pending.map((r, idx) => {
              const budget = formatBudget(r.budget_offered)
              const sched = formatScheduledLine(r)
              const addr = formatAddress(r)
              return (
                <motion.article key={r.assignment_id}
                  initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: Math.min(idx * 0.05, 0.2) }}
                  style={{ background: 'var(--surface)', border: '1px solid var(--accent-border)', borderRadius: 12, padding: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                    <div style={{ flexShrink: 0, width: 36, height: 36, borderRadius: 8, background: 'var(--accent-dim)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Briefcase style={{ width: 16, height: 16, color: 'var(--accent-text)' }} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <h2 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: 'var(--ink-primary)' }}>{r.title ?? 'Service visit'}</h2>
                      {r.work_order_number && (
                        <p style={{ margin: '2px 0 0', fontSize: 11, color: 'var(--ink-tertiary)' }}>WO {r.work_order_number}{r.purchase_order_number ? ` · PO ${r.purchase_order_number}` : ''}</p>
                      )}
                    </div>
                  </div>

                  {budget && (
                    <div style={{ display: 'inline-flex', alignSelf: 'flex-start', alignItems: 'center', gap: 6, padding: '5px 10px', background: 'var(--accent-dim)', border: '1px solid var(--accent-border)', borderRadius: 999 }}>
                      <DollarSign style={{ width: 12, height: 12, color: 'var(--accent-text)' }} />
                      <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--accent-text)' }}>{budget}</span>
                    </div>
                  )}

                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    {sched && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 13, color: 'var(--ink-secondary)' }}>
                        <CalendarClock style={{ width: 13, height: 13, color: 'var(--ink-tertiary)' }} />
                        {sched}
                      </div>
                    )}
                    {addr && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 13, color: 'var(--ink-secondary)' }}>
                        <MapPin style={{ width: 13, height: 13, color: 'var(--ink-tertiary)' }} />
                        {addr}
                      </div>
                    )}
                  </div>

                  {r.job_notes && (
                    <div style={{ padding: '10px 12px', background: 'var(--hover-bg)', borderRadius: 8 }}>
                      <p style={{ margin: 0, fontSize: 11, color: 'var(--ink-tertiary)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Job details</p>
                      <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--ink-primary)', whiteSpace: 'pre-wrap' }}>{r.job_notes}</p>
                    </div>
                  )}

                  <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                    <button type="button"
                      disabled={respond.isPending}
                      onClick={() => respond.mutate({ op: 'accept_session', assignment_id: r.assignment_id })}
                      style={{ flex: 1, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 7, padding: '10px', background: 'var(--accent)', border: 'none', borderRadius: 9, color: 'var(--accent-ink)', fontSize: 13, fontWeight: 700, cursor: respond.isPending ? 'wait' : 'pointer', opacity: respond.isPending ? 0.7 : 1 }}>
                      <CheckCircle2 style={{ width: 14, height: 14 }} />
                      Accept
                    </button>
                    <button type="button"
                      disabled={respond.isPending}
                      onClick={() => { setDeclineOpen(r); setDeclineReason('') }}
                      style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 7, padding: '10px 14px', background: 'var(--hover-bg)', border: '1px solid var(--border-default)', borderRadius: 9, color: 'var(--ink-secondary)', fontSize: 13, fontWeight: 600, cursor: respond.isPending ? 'wait' : 'pointer' }}>
                      <XCircle style={{ width: 14, height: 14 }} />
                      Decline
                    </button>
                  </div>
                </motion.article>
              )
            })}
          </div>
        )}
      </section>

      {/* Upcoming — accepted assigned + scheduled self-entered. "+ New job"
          affordance lives in this section's header — discoverability sits
          next to the section that will display the new entries. */}
      <section>
        <header style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10, gap: 12 }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
            <h2 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: 'var(--ink-primary)' }}>Upcoming work</h2>
            <span style={{ fontSize: 12, color: 'var(--ink-tertiary)' }}>{upcoming.length}</span>
          </div>
          <button type="button" onClick={() => setNewJobOpen(true)}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 12px', background: 'var(--accent-dim)', border: '1px solid var(--accent-border)', borderRadius: 8, color: 'var(--accent-text)', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
            <Plus style={{ width: 13, height: 13 }} />
            New job
          </button>
        </header>
        {upcoming.length === 0 ? (
          <div style={{ padding: 14, background: 'var(--surface)', border: '1px solid var(--border-subtle)', borderRadius: 12, color: 'var(--ink-tertiary)', fontSize: 12 }}>
            Nothing scheduled yet.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {upcoming.map((r) => {
              const sched = formatScheduledLine(r)
              const isSelf = r.source === 'self_entered'
              if (isSelf) {
                // Self-entered card: title=private_label, Lock badge marks it
                // as the vendor's own private entry. No budget, no lifecycle
                // chip, no address (Phase 1 schema doesn't carry them).
                return (
                  <Link key={r.assignment_id} to={`/vendor/jobs/${r.assignment_id}`}
                    style={{ background: 'var(--surface)', border: '1px solid var(--border-subtle)', borderRadius: 10, padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 4, textDecoration: 'none' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
                      <h3 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: 'var(--ink-primary)' }}>{r.title ?? 'Private job'}</h3>
                      <span title="Your private job — staff can't see the details"
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 8px', background: 'var(--hover-bg)', border: '1px solid var(--border-default)', borderRadius: 999, fontSize: 10, fontWeight: 700, color: 'var(--ink-tertiary)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                        <Lock style={{ width: 10, height: 10 }} />
                        Private
                      </span>
                    </div>
                    {sched && <span style={{ fontSize: 12, color: 'var(--ink-secondary)' }}>{sched}</span>}
                  </Link>
                )
              }
              // Assigned card — byte-unchanged from V2c.
              const addr = formatAddress(r)
              const budget = formatBudget(r.budget_offered)
              return (
                <Link key={r.assignment_id} to={`/vendor/jobs/${r.assignment_id}`}
                  style={{ background: 'var(--surface)', border: '1px solid var(--border-subtle)', borderRadius: 10, padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 4, textDecoration: 'none' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
                    <h3 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: 'var(--ink-primary)' }}>{r.title ?? 'Service visit'}</h3>
                    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                      {r.latest_event_type && <LifecycleChip type={r.latest_event_type} at={r.latest_event_at ?? undefined} />}
                      {budget && <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--accent-text)' }}>{budget}</span>}
                    </div>
                  </div>
                  {sched && <span style={{ fontSize: 12, color: 'var(--ink-secondary)' }}>{sched}</span>}
                  {addr && <span style={{ fontSize: 12, color: 'var(--ink-tertiary)' }}>{addr}</span>}
                </Link>
              )
            })}
          </div>
        )}
      </section>

      {/* New-job modal — vendor enters their own private job. Same scaffold
          as the Delay/Return modals in VendorJobDetail. */}
      {newJobOpen && vendorUser && (
        <NewSelfJobModal
          onClose={() => setNewJobOpen(false)}
          vendorUser={vendorUser}
        />
      )}

      {/* Decline modal (assigned job action — unchanged) */}
      {declineOpen && (
        <div onClick={() => { if (!respond.isPending) setDeclineOpen(null) }}
          style={{ position: 'fixed', inset: 0, background: 'var(--overlay-scrim)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', padding: 16, zIndex: 50 }}>
          <motion.div onClick={e => e.stopPropagation()}
            initial={{ y: 30, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.22 }}
            style={{ background: 'var(--surface)', borderRadius: 16, padding: 20, width: '100%', maxWidth: 460, boxShadow: 'var(--shadow-popover-high)', display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <AlertCircle style={{ width: 16, height: 16, color: 'var(--warning)' }} />
              <h2 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: 'var(--ink-primary)' }}>Decline this job?</h2>
            </div>
            <p style={{ margin: 0, fontSize: 13, color: 'var(--ink-secondary)' }}>
              Optional — let the operator know why. They'll see this on their end.
            </p>
            <textarea value={declineReason}
              onChange={e => setDeclineReason(e.target.value)}
              placeholder="Reason (optional)"
              rows={4}
              maxLength={DECLINE_MAX}
              style={{ width: '100%', padding: '10px 12px', background: 'var(--field-bg)', border: '1px solid var(--border-default)', borderRadius: 9, fontSize: 13, color: 'var(--ink-primary)', outline: 'none', resize: 'vertical', boxSizing: 'border-box', fontFamily: 'inherit' }} />
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button type="button" onClick={() => setDeclineOpen(null)} disabled={respond.isPending}
                style={{ padding: '9px 14px', background: 'transparent', border: '1px solid var(--border-default)', borderRadius: 8, color: 'var(--ink-secondary)', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                Cancel
              </button>
              <button type="button"
                disabled={respond.isPending}
                onClick={() => respond.mutate({ op: 'decline_session', assignment_id: declineOpen.assignment_id, reason: declineReason.trim() || null })}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 7, padding: '9px 14px', background: 'var(--danger)', border: 'none', borderRadius: 8, color: 'var(--ink-inverse)', fontSize: 13, fontWeight: 700, cursor: respond.isPending ? 'wait' : 'pointer' }}>
                {respond.isPending && <Loader2 style={{ width: 13, height: 13, animation: 'spin 1s linear infinite' }} />}
                Decline job
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  )
}

// ─── NewSelfJobModal ────────────────────────────────────────────────────────
// Vendor's own private job entry. Direct INSERT into vendor_self_jobs;
// Phase 1's RLS (vendor_self_insert WITH CHECK) gates the row. The
// resolved scope (get_vendor_id / get_vendor_workspace_id / get_vendor_user_id)
// must match the inserted columns, which is true by construction when the
// vendorUser session is the writer.
//
// Scaffold mirrors VendorJobDetail's Delay / Return / Confirm trio:
// fixed scrim (zIndex 50, below modals overlap), bottom-anchored card,
// motion slide-up, var(--surface), max-width 460. Date and time inputs
// use the native pickers (input type=date/time) — established pattern in
// NewJobModal.tsx and the vendor portal's Return-visit modal.
const PRIVATE_LABEL_MAX = 120

interface NewSelfJobForm {
  private_label: string
  scheduled_date: string
  scheduled_start: string
  scheduled_end: string
}

function NewSelfJobModal({
  onClose,
  vendorUser,
}: {
  onClose: () => void
  vendorUser: { id: string; vendor_id: string; workspace_id: string }
}) {
  const queryClient = useQueryClient()
  const [form, setForm] = useState<NewSelfJobForm>({
    private_label: '',
    scheduled_date: '',
    scheduled_start: '',
    scheduled_end: '',
  })

  // Narrow detection — stack the time grid <400px to match the bottom-bar
  // breakpoint used elsewhere in the vendor portal.
  const [narrow, setNarrow] = useState(() =>
    typeof window !== 'undefined' && window.matchMedia('(max-width: 400px)').matches
  )
  useEffect(() => {
    if (typeof window === 'undefined') return
    const mq = window.matchMedia('(max-width: 400px)')
    const handler = (e: MediaQueryListEvent) => setNarrow(e.matches)
    mq.addEventListener('change', handler)
    return () => mq.removeEventListener('change', handler)
  }, [])

  const create = useMutation({
    mutationFn: async () => {
      const trimmed = form.private_label.trim()
      // Time/end sanity — start must be < end. Same-time or end-before-start
      // produces an empty busy range (and is a likely UX mistake). DB has
      // no CHECK for this; UI catches it here.
      if (form.scheduled_start >= form.scheduled_end) {
        throw new Error('End time must be after start time.')
      }
      const { error } = await supabase.from('vendor_self_jobs').insert({
        vendor_id: vendorUser.vendor_id,
        workspace_id: vendorUser.workspace_id,
        vendor_user_id: vendorUser.id,
        private_label: trimmed,
        scheduled_date: form.scheduled_date,
        scheduled_start: form.scheduled_start,
        scheduled_end: form.scheduled_end,
        // status defaults to 'scheduled' server-side; do not set explicitly.
      })
      if (error) throw error
    },
    onSuccess: () => {
      toast.success('Job added')
      queryClient.invalidateQueries({ queryKey: ['vendor-jobs'] })
      onClose()
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : 'Could not add job')
    },
  })

  const canSubmit =
    form.private_label.trim().length > 0
    && form.private_label.trim().length <= PRIVATE_LABEL_MAX
    && form.scheduled_date.length > 0
    && form.scheduled_start.length > 0
    && form.scheduled_end.length > 0
    && !create.isPending

  return (
    <div onClick={() => { if (!create.isPending) onClose() }}
      style={{ position: 'fixed', inset: 0, background: 'var(--overlay-scrim)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', padding: 16, zIndex: 50 }}>
      <motion.div onClick={e => e.stopPropagation()}
        initial={{ y: 30, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.22 }}
        style={{ background: 'var(--surface)', borderRadius: 16, padding: 20, width: '100%', maxWidth: 460, boxShadow: 'var(--shadow-popover-high)', display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Plus style={{ width: 16, height: 16, color: 'var(--accent-text)' }} />
          <h2 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: 'var(--ink-primary)' }}>Add a private job</h2>
        </div>
        <p style={{ margin: 0, fontSize: 13, color: 'var(--ink-secondary)' }}>
          Block out time for your own work. The job details stay private — staff sees only the time window.
        </p>

        <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Label</span>
          <input type="text" autoFocus
            value={form.private_label}
            onChange={e => setForm(f => ({ ...f, private_label: e.target.value }))}
            maxLength={PRIVATE_LABEL_MAX}
            placeholder="Smith bathroom remodel"
            style={{ padding: '10px 12px', background: 'var(--field-bg)', border: '1px solid var(--border-default)', borderRadius: 9, fontSize: 13, color: 'var(--ink-primary)', outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit' }} />
        </label>

        <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Date</span>
          <input type="date"
            value={form.scheduled_date}
            onChange={e => setForm(f => ({ ...f, scheduled_date: e.target.value }))}
            style={{ padding: '10px 12px', background: 'var(--field-bg)', border: '1px solid var(--border-default)', borderRadius: 9, fontSize: 13, color: 'var(--ink-primary)', outline: 'none', boxSizing: 'border-box' }} />
        </label>

        <div style={{ display: 'grid', gridTemplateColumns: narrow ? '1fr' : '1fr 1fr', gap: 10 }}>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Start</span>
            <input type="time"
              value={form.scheduled_start}
              onChange={e => setForm(f => ({ ...f, scheduled_start: e.target.value }))}
              style={{ padding: '10px 12px', background: 'var(--field-bg)', border: '1px solid var(--border-default)', borderRadius: 9, fontSize: 13, color: 'var(--ink-primary)', outline: 'none', boxSizing: 'border-box' }} />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>End</span>
            <input type="time"
              value={form.scheduled_end}
              onChange={e => setForm(f => ({ ...f, scheduled_end: e.target.value }))}
              style={{ padding: '10px 12px', background: 'var(--field-bg)', border: '1px solid var(--border-default)', borderRadius: 9, fontSize: 13, color: 'var(--ink-primary)', outline: 'none', boxSizing: 'border-box' }} />
          </label>
        </div>

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 4 }}>
          <button type="button" onClick={onClose} disabled={create.isPending}
            style={{ padding: '9px 14px', background: 'transparent', border: '1px solid var(--border-default)', borderRadius: 8, color: 'var(--ink-secondary)', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
            Cancel
          </button>
          <button type="button"
            disabled={!canSubmit}
            onClick={() => create.mutate()}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 7, padding: '9px 14px', background: 'var(--accent)', border: 'none', borderRadius: 8, color: 'var(--accent-ink)', fontSize: 13, fontWeight: 700, cursor: canSubmit ? 'pointer' : 'not-allowed', opacity: canSubmit ? 1 : 0.5 }}>
            {create.isPending && <Loader2 style={{ width: 13, height: 13, animation: 'spin 1s linear infinite' }} />}
            Add job
          </button>
        </div>
      </motion.div>
    </div>
  )
}
